import React from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../../shared/ui/page-header/page-header.jsx';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import { getEnumOptionLabel } from '../../../shared/i18n/enum-labels.js';

const SECURITY_ALERT_STATUSES = ['open', 'acknowledged', 'resolved', 'false_positive'];
const SECURITY_ALERT_SEVERITIES = ['low', 'medium', 'high', 'critical'];

function EnumOptions({ values, t, namespace }) {
  return values.map((value) => (
    <option key={value} value={value}>{getEnumOptionLabel(t, namespace, value)}</option>
  ));
}

function SecurityAlertDetailPanel({
  alert,
  isOpen,
  isLoading,
  isStatusSaving,
  error,
  canManage,
  statusNote,
  closeDetail,
  updateStatusNote,
  updateAlertStatus,
}) {
  const { t } = useTranslation();

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div className="offcanvas-backdrop fade show" onClick={closeDetail} />
      <aside
        className="offcanvas offcanvas-end show"
        style={{ visibility: 'visible', width: 'min(720px, 100vw)' }}
        aria-modal="true"
        role="dialog"
      >
        <div className="offcanvas-header border-bottom">
          <div>
            <h5 className="offcanvas-title">{t('securityAlerts.detailTitle')}</h5>
            <div className="text-muted small">{alert?.ruleLabel || t('common.loading')}</div>
          </div>
          <button type="button" className="btn-close" aria-label={t('common.close')} onClick={closeDetail} />
        </div>
        <div className="offcanvas-body">
          {error ? <div className="alert alert-danger py-2">{error}</div> : null}
          {isLoading && !alert ? <div className="text-muted">{t('common.loading')}</div> : null}
          {alert ? (
            <>
              <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                <div>
                  <div className="hstack gap-2 flex-wrap mb-2">
                    <span className={`badge ${alert.severityBadgeClass}`}>{alert.severityLabel}</span>
                    <span className={`badge ${alert.statusBadgeClass}`}>{alert.statusLabel}</span>
                    <span className="badge bg-dark-subtle text-dark">{alert.score}</span>
                  </div>
                  <div className="fw-semibold">{alert.ruleLabel}</div>
                  <div className="font-monospace small text-muted text-break">{alert.ruleId}</div>
                </div>
                <div className="text-end small">
                  <div className="text-muted">{t('securityAlerts.created')}</div>
                  <div>{alert.createdAtLabel}</div>
                </div>
              </div>

              <div className="row g-3 mb-4">
                <div className="col-md-6">
                  <div className="text-muted small">{t('securityAlerts.actor')}</div>
                  <div className="font-monospace small text-break">{alert.actorLabel}</div>
                </div>
                <div className="col-md-3">
                  <div className="text-muted small">{t('securityAlerts.affectedProfiles')}</div>
                  <div>{alert.affectedCount}</div>
                </div>
                <div className="col-md-3">
                  <div className="text-muted small">{t('securityAlerts.ip')}</div>
                  <div className="font-monospace small text-break">{alert.ip}</div>
                </div>
                <div className="col-12">
                  <div className="text-muted small">{t('securityAlerts.window')}</div>
                  <div>{alert.windowLabel}</div>
                </div>
              </div>

              <h6 className="mb-2">{t('securityAlerts.evidenceSummary')}</h6>
              {alert.evidenceSummary.length > 0 ? (
                <ul className="list-unstyled small mb-4">
                  {alert.evidenceSummary.map((item) => (
                    <li key={item} className="border-bottom py-1">{item}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted small mb-4">{t('securityAlerts.noEvidence')}</p>
              )}

              <h6 className="mb-2">{t('securityAlerts.sampleProfiles')}</h6>
              {alert.sampleProfileUids.length > 0 ? (
                <div className="d-flex flex-wrap gap-2 mb-4">
                  {alert.sampleProfileUids.map((uid) => (
                    <a key={uid} className="badge bg-primary-subtle text-primary text-decoration-none" href={`/profiles/${encodeURIComponent(uid)}`}>
                      {uid}
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-muted small mb-4">{t('securityAlerts.noSampleProfiles')}</p>
              )}

              <h6 className="mb-2">{t('securityAlerts.suggestedResponse')}</h6>
              <p className="small mb-4">{alert.suggestedResponse || '-'}</p>

              {canManage ? (
                <div className="border-top pt-3">
                  <label className="form-label" htmlFor="security-alert-status-note">{t('securityAlerts.statusNote')}</label>
                  <textarea
                    id="security-alert-status-note"
                    className="form-control mb-3"
                    rows={3}
                    maxLength={1024}
                    value={statusNote}
                    onChange={(event) => updateStatusNote(event.target.value)}
                  />
                  <div className="d-flex flex-wrap gap-2">
                    <button type="button" className="btn btn-outline-warning btn-sm" onClick={() => updateAlertStatus('acknowledged')} disabled={isStatusSaving}>
                      {t('securityAlerts.acknowledge')}
                    </button>
                    <button type="button" className="btn btn-outline-success btn-sm" onClick={() => updateAlertStatus('resolved')} disabled={isStatusSaving}>
                      {t('securityAlerts.resolve')}
                    </button>
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => updateAlertStatus('false_positive')} disabled={isStatusSaving}>
                      {t('securityAlerts.falsePositive')}
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </aside>
    </>
  );
}

function SecurityAlertsPageView({
  rows,
  filters,
  total,
  isLoading,
  isDetailLoading,
  isStatusSaving,
  error,
  success,
  detailError,
  detailAlert,
  isDetailOpen,
  statusNote,
  canManageSecurityAlerts,
  updateFilter,
  applyFilters,
  resetFilters,
  refresh,
  openDetail,
  closeDetail,
  updateStatusNote,
  updateAlertStatus,
}) {
  const { t } = useTranslation();

  return (
    <>
      <FloatingAlerts error={error} success={success} />
      <PageHeader title={t('securityAlerts.title')} subtitle={t('securityAlerts.subtitle')} />

      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
            <div>
              <h5 className="mb-1">{t('securityAlerts.filters')}</h5>
              <p className="text-muted mb-0">{t('securityAlerts.filtersSubtitle')}</p>
            </div>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={refresh} disabled={isLoading}>
              <i className="ti ti-refresh me-1" />
              {t('securityAlerts.refresh')}
            </button>
          </div>

          <div className="row g-3 align-items-end">
            <div className="col-lg-3 col-md-4">
              <label className="form-label" htmlFor="security-alert-status">{t('securityAlerts.status')}</label>
              <select
                id="security-alert-status"
                className="form-select"
                value={filters.status}
                onChange={(event) => updateFilter('status', event.target.value)}
              >
                <option value="">{t('securityAlerts.anyStatus')}</option>
                <EnumOptions values={SECURITY_ALERT_STATUSES} t={t} namespace="securityAlertStatus" />
              </select>
            </div>
            <div className="col-lg-3 col-md-4">
              <label className="form-label" htmlFor="security-alert-severity">{t('securityAlerts.severity')}</label>
              <select
                id="security-alert-severity"
                className="form-select"
                value={filters.severity}
                onChange={(event) => updateFilter('severity', event.target.value)}
              >
                <option value="">{t('securityAlerts.anySeverity')}</option>
                <EnumOptions values={SECURITY_ALERT_SEVERITIES} t={t} namespace="securityAlertSeverity" />
              </select>
            </div>
            <div className="col-lg-3 col-md-4 d-flex gap-2">
              <button type="button" className="btn btn-primary" onClick={applyFilters} disabled={isLoading}>
                <i className="ti ti-filter me-1" />
                {t('securityAlerts.applyFilters')}
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={resetFilters} disabled={isLoading}>
                {t('common.reset')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
            <div>
              <h5 className="mb-1">{t('securityAlerts.activeAlerts')}</h5>
              <p className="text-muted mb-0">{t('securityAlerts.activeAlertsSubtitle')}</p>
            </div>
            <span className="badge bg-light text-dark">{total}</span>
          </div>

          <div className="table-responsive">
            <table className="table search-table align-middle mb-0">
              <thead>
                <tr>
                  <th>{t('securityAlerts.severity')}</th>
                  <th>{t('securityAlerts.status')}</th>
                  <th>{t('securityAlerts.rule')}</th>
                  <th>{t('securityAlerts.actor')}</th>
                  <th>{t('securityAlerts.score')}</th>
                  <th>{t('securityAlerts.affectedProfiles')}</th>
                  <th>{t('securityAlerts.ip')}</th>
                  <th>{t('securityAlerts.created')}</th>
                  <th className="text-end">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={9} className="text-muted">{t('common.loading')}</td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-muted">{t('securityAlerts.noAlerts')}</td>
                  </tr>
                ) : rows.map((row) => (
                  <tr key={row.uid}>
                    <td><span className={`badge ${row.severityBadgeClass}`}>{row.severityLabel}</span></td>
                    <td><span className={`badge ${row.statusBadgeClass}`}>{row.statusLabel}</span></td>
                    <td>
                      <div className="fw-semibold">{row.ruleLabel}</div>
                      <div className="font-monospace small text-muted text-break">{row.ruleId}</div>
                    </td>
                    <td className="font-monospace small text-break">{row.actorLabel}</td>
                    <td>{row.score}</td>
                    <td>{row.affectedCount}</td>
                    <td className="font-monospace small text-break">{row.ip}</td>
                    <td className="text-nowrap">{row.createdAtLabel}</td>
                    <td className="text-end">
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openDetail(row)}>
                        {t('securityAlerts.details')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <SecurityAlertDetailPanel
        alert={detailAlert}
        isOpen={isDetailOpen}
        isLoading={isDetailLoading}
        isStatusSaving={isStatusSaving}
        error={detailError}
        canManage={canManageSecurityAlerts}
        statusNote={statusNote}
        closeDetail={closeDetail}
        updateStatusNote={updateStatusNote}
        updateAlertStatus={updateAlertStatus}
      />
    </>
  );
}

export default SecurityAlertsPageView;
